import { Link } from "react-router-dom";
import { CalendarCheck, ShieldCheck, CreditCard } from "lucide-react";
import { IMAGES } from "@/lib/api";
import { ArmBar } from "@/components/site/Chrome";
import { ContactSection } from "@/components/site/Contact";
import { ShortletCard } from "@/components/site/ShortletCard";
import { CurrencySwitcher } from "@/components/site/CurrencySwitcher";
import { useCollection, usePageMeta } from "@/hooks/useData";

const STEPS = [
  { Icon: CalendarCheck, t: "Pick your dates", d: "Choose an apartment and the nights you want. Taken dates are crossed out." },
  { Icon: ShieldCheck, t: "We approve", d: "Our team checks your request and emails you a secure payment link." },
  { Icon: CreditCard, t: "Pay and stay", d: "Pay by card or Paystack within 48 hours and your dates are confirmed." },
];

const Steps = () => (
  <ol className="grid md:grid-cols-3 gap-6 mt-14" data-testid="shortlets-steps">
    {STEPS.map(({ Icon, t, d }, i) => (
      <li key={t} className="border-t border-gw-rule/40 pt-5">
        <p className="lab text-gw-accent inline-flex items-center gap-2"><Icon size={14} /> Step {i + 1}</p>
        <p className="lux text-2xl mt-3">{t}</p>
        <p className="text-sm mt-2 text-gw-accent/80">{d}</p>
      </li>
    ))}
  </ol>
);

export default function Shortlets() {
  usePageMeta("Shortlet Apartments You Can Book Online | Wolfe Realtors",
    "Wolfe Realtors shortlet apartments: choose your dates online, send a booking request and pay securely by card or Paystack once your stay is approved.",
    "https://www.greywolfeng.com/api/share/page/shortlets/image.jpg");
  const items = useCollection("shortlets");
  return (
    <div className="page-in">
      <section className="bg-gw-paper text-gw-ink py-24 md:py-32 border-t-8 border-gw-rule" data-testid="shortlets-page">
        <div className="max-w-6xl mx-auto px-6">
          <ArmBar current="realty" light />
          <div className="grid md:grid-cols-2 gap-12 items-center mt-12">
            <div className="min-w-0">
              <p className="lab text-gw-accent">Wolfe Realtors · Shortlets</p>
              <h1 className="lux gw-h1 mt-4">
                <span className="ln"><span>Stay a night,</span></span>
                <span className="ln"><span>or a month.</span></span>
              </h1>
              <p className="mt-6 gw-body" data-testid="shortlets-intro">Serviced shortlet apartments you can book online: request your dates, we approve, you pay securely.</p>
              <a href="#apartments" data-testid="shortlets-browse-link" className="group inline-flex gap-3 mt-10 lab bg-gw-btn text-gw-btnfg px-6 py-4 transition-colors duration-200 hover:bg-gw-btnhover">
                See apartments <span className="transition-transform duration-200 group-hover:translate-y-0.5">↓</span>
              </a>
            </div>
            <figure className="aspect-[4/3] md:aspect-[3/4] bg-gw-soft overflow-hidden m-0">
              <img src={IMAGES.hero2} alt="Bright serviced apartment living room" className="w-full h-full object-cover" />
            </figure>
          </div>
          <Steps />
        </div>
      </section>
      <section id="apartments" className="bg-gw-paper2 text-gw-ink py-24 md:py-32 scroll-mt-16" data-testid="shortlets-list-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <h2 className="lux gw-h2">Available apartments</h2>
            <CurrencySwitcher testPrefix="shortlets-currency" />
          </div>
          {items === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] bg-gw-card animate-pulse" />)}</div>
          ) : items.length === 0 ? (
            <p className="gw-body text-gw-accent/70" data-testid="shortlets-empty">No shortlets listed right now. <Link to="/realty#contact" className="underline underline-offset-4">Send us an enquiry</Link> and we'll find you a stay.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shortlets-grid">
              {items.map((s, i) => <ShortletCard key={s.id} s={s} idx={i} />)}
            </div>
          )}
        </div>
      </section>
      <ContactSection defaultCompany="Wolfe Realtors" />
    </div>
  );
}
